import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/opportunities", label: "Opportunities" },
    { to: "/technology", label: "Technology" },
    { to: "/for-investors", label: "For Investors" },
    { to: "/for-partners", label: "For Partners" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled ? "border-b border-black/5 bg-white/70 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
                    <div className="flex items-center gap-12 xl:gap-16">
                        <Link to="/" data-testid="header-logo" className="group flex items-baseline gap-2" aria-label="LUXATI home">
                            <span className={`font-serif text-2xl font-medium tracking-[0.22em] transition-colors duration-500 ${scrolled ? "text-ink" : "text-ivory"}`}>LUXATI</span>
                        </Link>
                        <nav className="hidden items-center gap-6 xl:flex 2xl:gap-8" data-testid="desktop-nav">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `link-underline whitespace-nowrap text-[12px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                                        scrolled
                                            ? isActive
                                                ? "text-ink"
                                                : "text-ink/60 hover:text-ink"
                                            : isActive
                                              ? "text-ivory"
                                              : "text-ivory/70 hover:text-ivory"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="header-cta"
                            className={`group flex items-center gap-2 whitespace-nowrap border px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-500 ${scrolled ? "border-ink/80 text-ink hover:bg-ink hover:text-ivory" : "border-ivory/60 text-ivory hover:bg-ivory hover:text-ink"}`}
                        >
                            Speak With LUXATI
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        </nav>
                    </div>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(true)}
                        className={`flex items-center gap-2 transition-colors duration-500 xl:hidden ${scrolled ? "text-ink" : "text-ivory"}`}
                        aria-label="Open menu"
                    >
                        <span className="text-[11px] uppercase tracking-[0.25em]">Menu</span>
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[60] flex flex-col bg-charcoal text-ivory"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="flex items-center justify-between px-6 py-5">
                            <span className="font-serif text-2xl font-medium tracking-[0.22em]">LUXATI</span>
                            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
                            {NAV.map((item, i) => (
                                <motion.div
                                    key={item.to}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={item.to}
                                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="block border-b border-ivory/10 py-4 font-serif text-4xl font-light transition-colors duration-300 hover:text-gold"
                                    >
                                        {item.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <p className="px-8 pb-10 text-[10px] uppercase tracking-[0.3em] text-ivory/40">
                            India &bull; Dubai
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
